// shared/renderSeatDots.js
import React from 'react';

import i18n from "../../i18n";
const tr = i18n.t.bind(i18n);
/**
 * For tables already assigned to a reservation (Service view):
 * Distribute reservation.people evenly across its tables (by reserved_id), cap by capacity.
 */
export const getReservedCount = (table, tables = [], reservations = []) => {
  if (!table || !table.reserved_id) return 0;

  const res = (reservations || []).find(
    r => Number(r.id) === Number(table.reserved_id)
  );
  const people = Math.max(0, parseInt(res?.people ?? 0, 10) || 0);
  if (people <= 0) return 0;

  const group = (tables || [])
    .filter(t => Number(t?.reserved_id) === Number(table.reserved_id))
    .sort((a, b) => Number(a.id) - Number(b.id));

  const n = group.length || 1;
  const base = Math.floor(people / n);
  const extra = people % n;
  const idx = Math.max(0, group.findIndex(t => Number(t.id) === Number(table.id)));

  const cap = Math.max(0, parseInt(table.capacity ?? 0, 10) || 0);
  const count = base + (idx < extra ? 1 : 0);
  return Math.min(cap, Math.max(0, count));
};

/**
 * For TableSelector (ReservationForm / WalkedIn):
 * Distribute the *current* reservation's people across the currently selected table IDs.
 * Returns a { [tableId]: count } map.
 */
export const computeSelectionCounts = (people, selectedTableIds = [], tables = []) => {
  const ids = (selectedTableIds || []).map(Number);
  const selected = (tables || []).filter(t => ids.includes(Number(t.id))).sort((a,b) => Number(a.id) - Number(b.id));

  const p = Math.max(0, parseInt(people ?? 0, 10) || 0);
  if (p === 0 || selected.length === 0) return {};

  const n = selected.length;
  const base = Math.floor(p / n);
  let extra = p % n;

  const out = {};
  selected.forEach(t => {
    const cap = Math.max(0, parseInt(t.capacity ?? 0, 10) || 0);
    let c = base + (extra > 0 ? 1 : 0);
    if (extra > 0) extra--;
    out[Number(t.id)] = Math.min(cap, Math.max(0, c));
  });
  return out;
};

/**
 * Seat dots renderer with optional highlighting.
 * options.reservedCount -> how many seats to color
 * options.reservedClass -> CSS class to apply to colored seats (default: 'reserved-seat-red')
 */
export const renderSeatDots = (table, offSetX, offSetY, options = {}, mult = 1) => {
  const reservedCount = Math.max(0, parseInt(options.reservedCount ?? 0, 10) || 0);
  const reservedClass = options.reservedClass || 'reserved-seat-red';

  const dots = [];
  const total = Math.max(1, parseInt(table.capacity));
  const width = table.width*mult;
  const height = table.height*mult;
  const seatSpacing = 15;
  const seatOffset = -1;
  let seatIndex = 0;

  const maxTop = Math.floor(width / seatSpacing);
  const maxBottom = maxTop;
  const maxLeft = Math.floor(height / seatSpacing);
  const maxRight = maxLeft;

  const maxTotal = maxTop + maxBottom + maxLeft + maxRight;
  const clampedTotal = Math.min(total, maxTotal);

  const seatsPerSide = { top: 0, right: 0, bottom: 0, left: 0 };
  const order = ['top', 'bottom', 'left', 'right'];
  let i = 0, assigned = 0;
  while (assigned < clampedTotal) {
    const side = order[i % 4];
    const maxForSide = side === 'top' || side === 'bottom' ? maxTop : maxLeft;
    if (seatsPerSide[side] < maxForSide) {
      seatsPerSide[side]++;
      assigned++;
    }
    i++;
    if (i > 100) break;
  }

  // Top
  for (let i = 0; i < seatsPerSide.top; i++) {
    const x = ((i + 1) / (seatsPerSide.top + 1)) * width;
    const cls = tr('seat-dot {{var1}}', {
      var1: seatIndex < reservedCount ? reservedClass : ''
    });
    dots.push(<div key={`top-${i}`} className={cls} style={{ top: tr('-2px'), left: tr('{{x}}px', {
      x
    }) }} />);
    seatIndex++;
  }

  // Bottom
  for (let i = 0; i < seatsPerSide.bottom; i++) {
    const x = ((i + 1) / (seatsPerSide.bottom + 1)) * width;
    const cls = tr('seat-dot {{var1}}', {
      var1: seatIndex < reservedCount ? reservedClass : ''
    });
    dots.push(<div key={`bottom-${i}`} className={cls} style={{ top: tr('{{var1}}px', {
      var1: height - seatOffset + (offSetY || offSetX)
    }), left: tr('{{x}}px', {
      x
    }) }} />);
    seatIndex++;
  }

  // Left
  for (let i = 0; i < seatsPerSide.left; i++) {
    const y = ((i + 1) / (seatsPerSide.left + 1)) * height;
    const cls = tr('seat-dot {{var1}}', {
      var1: seatIndex < reservedCount ? reservedClass : ''
    });
    dots.push(<div key={`left-${i}`} className={cls} style={{ left: tr('-2px'), top: tr('{{y}}px', {
      y
    }) }} />);
    seatIndex++;
  }

  // Right
  for (let i = 0; i < seatsPerSide.right; i++) {
    const y = ((i + 1) / (seatsPerSide.right + 1)) * height;
    const cls = tr('seat-dot {{var1}}', {
      var1: seatIndex < reservedCount ? reservedClass : ''
    });
    dots.push(<div key={`right-${i}`} className={cls} style={{ left: tr('{{var1}}px', {
      var1: width - seatOffset + offSetX
    }), top: tr('{{y}}px', {
      y
    }) }} />);
    seatIndex++;
  }

  return dots;
};
